import { Effect, Option, Queue, Schema as S, Stream } from "effect";
import * as Subscription from "foldkit/subscription";
import posthog from "posthog-js";
import { Message } from "./message";
import type { Model } from "./model";

export const BILLING_PREVIEW_FLAG = "billing-preview";
type FlagClient = Pick<
	typeof posthog,
	| "onFeatureFlags"
	| "isFeatureEnabled"
	| "get_distinct_id"
	| "reloadFeatureFlags"
>;

/** UI rollout only: payment configuration and authorization remain on the server. */
export const watchBillingPreview = (
	client: FlagClient,
	userId: string,
	emit: (enabled: boolean) => void,
) => {
	const unsubscribe = client.onFeatureFlags((_flags, _variants, context) => {
		emit(
			!context?.errorsLoading &&
				client.get_distinct_id() === userId &&
				client.isFeatureEnabled(BILLING_PREVIEW_FLAG, { fresh: true }) === true,
		);
	});
	client.reloadFeatureFlags();
	return unsubscribe;
};

export const billingPreviewSubscriptions = Subscription.make<Model, Message>()(
	(entry) => ({
		billingPreview: entry(
			{ userId: S.Option(S.String), generation: S.Number },
			{
				modelToDependencies: (model) => ({
					userId:
						model.auth._tag === "SignedIn"
							? Option.some(model.auth.user.id)
							: Option.none(),
					generation: model.sessionGeneration,
				}),
				dependenciesToStream: ({ userId, generation }) =>
					Option.isNone(userId) || !posthog.__loaded
						? Stream.empty
						: Stream.unwrap(
								Effect.gen(function* () {
									const queue = yield* Queue.unbounded<boolean>();
									yield* Effect.acquireRelease(
										Effect.sync(() =>
											watchBillingPreview(posthog, userId.value, (enabled) =>
												Queue.offerUnsafe(queue, enabled),
											),
										),
										(unsubscribe) => Effect.sync(unsubscribe),
									);
									return Stream.fromQueue(queue).pipe(
										Stream.map((enabled) =>
											Message.BillingPreviewChanged({ generation, enabled }),
										),
									);
								}),
							),
			},
		),
	}),
);
